import { Button, Label, Switch, Textarea } from "@roberttamas/ui";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { ColorField } from "@/components/color-field";
import { Loader2 } from "@/components/icons";
import type { TranslationKey } from "@/i18n";
import { generateColorScheme } from "@/lib/api";
import {
  DEFAULT_OVERRIDES,
  DEFAULT_SEED,
  PALETTE_GROUPS,
  generatePalette,
  isHexColor,
  statusColorWarning,
  type OverrideKey,
  type Palette,
  type Seeds,
} from "@/lib/color-scheme";
import { toAppError } from "@/lib/errors";
import type { GenerateColorSchemeResp } from "@/lib/types";

type OptionalKey = OverrideKey;

const OPTIONAL_SEEDS: { key: OptionalKey; labelKey: TranslationKey }[] = [
  { key: "primary", labelKey: "colorEditor.families.primary" },
  { key: "secondary", labelKey: "colorEditor.families.secondary" },
  { key: "background", labelKey: "colorEditor.families.background" },
  { key: "error", labelKey: "colorEditor.families.error" },
  { key: "success", labelKey: "colorEditor.families.success" },
  { key: "warning", labelKey: "colorEditor.families.warning" },
];

/** Mood words used by the "I'm feeling lucky" button. */
const MOOD_WORDS = [
  "romantic",
  "rustic",
  "modern minimalist",
  "vintage",
  "garden party",
  "beachy",
  "moody and dramatic",
  "bohemian",
  "classic and timeless",
  "autumnal",
  "spring blossom",
  "midnight elegance",
];

/** Pick a random vivid-ish hex color. */
function randomHexColor(): string {
  const n = Math.floor(Math.random() * 0xffffff);
  return `#${n.toString(16).padStart(6, "0")}`;
}

/** The value a {@link ColorSchemeEditor} reads and emits. */
export interface ColorSchemeValue {
  seeds: Seeds;
  palette: Palette;
}

type OptionalState = Record<OptionalKey, string | null>;

function optionalFromSeeds(seeds: Seeds): OptionalState {
  return {
    primary: typeof seeds.primary === "string" ? seeds.primary : null,
    secondary: typeof seeds.secondary === "string" ? seeds.secondary : null,
    background: typeof seeds.background === "string" ? seeds.background : null,
    error: typeof seeds.error === "string" ? seeds.error : null,
    success: typeof seeds.success === "string" ? seeds.success : null,
    warning: typeof seeds.warning === "string" ? seeds.warning : null,
  };
}

function seedsFrom(seedValue: string, optional: OptionalState): Seeds {
  const seeds: Seeds = { seed: seedValue };
  for (const { key } of OPTIONAL_SEEDS) {
    const v = optional[key];
    if (v) seeds[key] = v;
  }
  return seeds;
}

interface ColorSchemeEditorProps {
  value: ColorSchemeValue;
  onChange: (next: ColorSchemeValue) => void;
}

/**
 * Controlled color-scheme editor with three ways to build a palette: an AI
 * prompt, a Material-style seed, or a detailed per-color picker. Emits the
 * resolved `{ seeds, palette }` on every change. Site- and template-agnostic —
 * callers own persistence.
 */
export function ColorSchemeEditor({ value, onChange }: ColorSchemeEditorProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"ai" | "seed" | "detailed">("ai");
  const [seed, setSeed] = useState(value.seeds.seed ?? DEFAULT_SEED);
  const [optional, setOptional] = useState<OptionalState>(() =>
    optionalFromSeeds(value.seeds),
  );
  const [aiMessage, setAiMessage] = useState("");
  const [aiSeed, setAiSeed] = useState<string | null>(null);
  const [aiWarnings, setAiWarnings] = useState<string[]>([]);

  const palette = value.palette;

  const emit = (nextPalette: Palette, nextSeeds: Seeds) => {
    onChange({ seeds: nextSeeds, palette: nextPalette });
  };

  const changeSeed = (v: string) => {
    setSeed(v);
    emit(palette, seedsFrom(v, optional));
  };

  const toggleOptional = (key: OptionalKey, on: boolean) => {
    const next: OptionalState = {
      ...optional,
      [key]: on ? DEFAULT_OVERRIDES[key] : null,
    };
    setOptional(next);
    emit(palette, seedsFrom(seed, next));
  };

  const changeOptional = (key: OptionalKey, v: string) => {
    const next: OptionalState = { ...optional, [key]: v };
    setOptional(next);
    emit(palette, seedsFrom(seed, next));
  };

  const regenerate = () => {
    if (!isHexColor(seed)) {
      toast.error(t("colorEditor.seedInvalid"));
      return;
    }
    const nextSeeds = seedsFrom(seed, optional);
    setAiWarnings([]);
    emit(generatePalette(nextSeeds), nextSeeds);
  };

  const setPaletteKey = (key: string, v: string) => {
    setAiWarnings([]);
    emit({ ...palette, [key]: v }, seedsFrom(seed, optional));
  };

  const aiMutation = useMutation<
    GenerateColorSchemeResp,
    unknown,
    { message?: string; seed?: string }
  >({
    mutationFn: (input) => generateColorScheme(input),
    onSuccess: (resp) => {
      setSeed(resp.seed);
      const nextWarnings = resp.warnings ?? [];
      setAiWarnings(nextWarnings);
      if (nextWarnings.length > 0) {
        toast.warning(t("colorEditor.aiWarn"));
      } else {
        toast.success(t("colorEditor.aiSuccess"));
      }
      emit(resp.palette, seedsFrom(resp.seed, optional));
    },
    onError: (err) => {
      setAiWarnings([]);
      toast.error(toAppError(err).message);
    },
  });

  const generateWithAi = () => {
    const message = aiMessage.trim();
    if (!message && !aiSeed) {
      toast.error(t("colorEditor.aiNeedInput"));
      return;
    }
    aiMutation.mutate({ message: message || undefined, seed: aiSeed ?? undefined });
  };

  const feelingLucky = () => {
    const luckyColor = randomHexColor();
    const luckyWord =
      MOOD_WORDS[Math.floor(Math.random() * MOOD_WORDS.length)] ?? "romantic";
    setAiSeed(luckyColor);
    setAiMessage(luckyWord);
    aiMutation.mutate({ message: luckyWord, seed: luckyColor });
  };

  const paletteValid = Object.keys(palette).length > 0 &&
    PALETTE_GROUPS.every((g) => g.keys.every((k) => isHexColor(palette[k] ?? "")));
  const warning = paletteValid ? statusColorWarning(palette) : null;

  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-md border p-0.5">
        <Button
          type="button"
          size="sm"
          variant={mode === "ai" ? "default" : "ghost"}
          onClick={() => setMode("ai")}
        >
          AI
        </Button>
        <Button
          type="button"
          size="sm"
          variant={mode === "seed" ? "default" : "ghost"}
          onClick={() => setMode("seed")}
        >
          {t("colorEditor.modeSeed")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant={mode === "detailed" ? "default" : "ghost"}
          onClick={() => setMode("detailed")}
        >
          {t("colorEditor.modeDetailed")}
        </Button>
      </div>

      {mode === "ai" && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ai-message">{t("aiTools.describeVibe")}</Label>
            <Textarea
              id="ai-message"
              rows={3}
              placeholder={t("colorEditor.aiPlaceholder")}
              value={aiMessage}
              onChange={(e) => setAiMessage(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t("colorEditor.aiHint")}
            </p>
          </div>
          <div className="space-y-2 rounded-md border p-3">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="ai-seed">{t("colorEditor.mainColorOptional")}</Label>
              <Switch
                id="ai-seed"
                checked={aiSeed !== null}
                onCheckedChange={(on) => setAiSeed(on ? DEFAULT_SEED : null)}
              />
            </div>
            {aiSeed !== null && (
              <ColorField
                label={t("colorEditor.mainColor")}
                hint={t("colorEditor.mainColorHint")}
                value={aiSeed}
                onChange={setAiSeed}
              />
            )}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              className="sm:flex-1"
              onClick={feelingLucky}
              disabled={aiMutation.isPending}
            >
              {t("aiTools.feelingLucky")}
            </Button>
            <Button
              type="button"
              className="sm:flex-1"
              onClick={generateWithAi}
              disabled={aiMutation.isPending || (!aiMessage.trim() && aiSeed === null)}
            >
              {aiMutation.isPending ? t("aiTools.generating") : t("aiTools.generateWithAi")}
            </Button>
          </div>
          <PalettePreview palette={palette} loading={aiMutation.isPending} />
          {aiWarnings.length > 0 && <ContrastWarnings warnings={aiWarnings} />}
        </div>
      )}

      {mode === "seed" && (
        <div className="space-y-4">
          <ColorField
            label={t("colorEditor.seedLabel")}
            hint={t("colorEditor.seedHint")}
            value={seed}
            onChange={changeSeed}
          />
          <div className="space-y-3 rounded-md border p-3">
            <p className="text-xs text-muted-foreground">
              {t("colorEditor.optionalOverrides")}
            </p>
            {OPTIONAL_SEEDS.map(({ key, labelKey }) => {
              const enabled = optional[key] !== null;
              return (
                <div key={key} className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor={`seed-${key}`}>{t(labelKey)}</Label>
                    <Switch
                      id={`seed-${key}`}
                      checked={enabled}
                      onCheckedChange={(on) => toggleOptional(key, on)}
                    />
                  </div>
                  {enabled && (
                    <ColorField
                      label={t("colorEditor.colorSuffix", { label: t(labelKey) })}
                      value={optional[key] as string}
                      onChange={(v) => changeOptional(key, v)}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <Button type="button" variant="outline" className="w-full" onClick={regenerate}>
            {t("colorEditor.generatePalette")}
          </Button>
          <PalettePreview palette={palette} />
        </div>
      )}

      {mode === "detailed" && (
        <div className="space-y-4">
          {PALETTE_GROUPS.map((group) => (
            <div key={group.family} className="space-y-3 rounded-lg border bg-muted/30 p-4">
              <div className="flex items-center gap-2 border-b pb-2">
                <span
                  className="inline-block h-4 w-4 shrink-0 rounded border border-border"
                  style={{
                    backgroundColor: isHexColor(palette[group.family] ?? "")
                      ? palette[group.family]
                      : "transparent",
                  }}
                />
                <h4 className="text-sm font-semibold">{t(group.labelKey)}</h4>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {group.keys.map((key) => (
                  <ColorField
                    key={key}
                    label={key}
                    hint={`--${key}`}
                    value={palette[key] ?? ""}
                    onChange={(v) => setPaletteKey(key, v)}
                  />
                ))}
              </div>
            </div>
          ))}
          <PalettePreview palette={palette} />
        </div>
      )}

      {warning && (
        <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">{warning}</p>
      )}
    </div>
  );
}

/** Advisory list of hard-to-read color pairs from an AI generation. */
function ContrastWarnings({ warnings }: { warnings: string[] }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1 rounded-md bg-muted px-3 py-2">
      <p className="text-xs font-medium text-foreground">
        {t("colorEditor.contrastHeading")}
      </p>
      <ul className="list-disc space-y-0.5 pl-4 text-xs text-muted-foreground">
        {warnings.map((w) => (
          <li key={w}>{w}</li>
        ))}
      </ul>
    </div>
  );
}

/** A small swatch with a hover label, used inside the grouped preview. */
function MiniSwatch({ keyName, color }: { keyName: string; color?: string }) {
  const valid = isHexColor(color ?? "");
  return (
    <span
      title={`--${keyName}: ${color ?? "-"}`}
      className="h-5 w-5 rounded border border-border"
      style={{ backgroundColor: valid ? color : "transparent" }}
    />
  );
}

/** One family card: a readable text sample over the base color plus variants. */
function FamilyPreview({
  group,
  palette,
}: {
  group: (typeof PALETTE_GROUPS)[number];
  palette: Palette;
}) {
  const { t } = useTranslation();
  const base = palette[group.family];
  const onColor = palette[`on-${group.family}`];
  const baseValid = isHexColor(base ?? "");
  const onValid = isHexColor(onColor ?? "");
  return (
    <div className="space-y-2 rounded-md border p-2">
      <div
        className="flex items-center justify-between rounded px-2 py-1.5 text-xs font-medium"
        style={{
          backgroundColor: baseValid ? base : "transparent",
          color: onValid ? onColor : undefined,
        }}
      >
        <span>{t(group.labelKey)}</span>
        <span className="opacity-80">Aa</span>
      </div>
      <div className="flex flex-wrap gap-1">
        {group.keys.map((key) => (
          <MiniSwatch key={key} keyName={key} color={palette[key]} />
        ))}
      </div>
    </div>
  );
}

/** Grouped, labeled preview with a readability sample per color family. */
export function PalettePreview({
  palette,
  loading = false,
}: {
  palette: Palette;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">{t("colorEditor.preview")}</p>
      <div className="relative">
        <div
          className={`grid gap-2 sm:grid-cols-2 transition-opacity ${
            loading ? "pointer-events-none opacity-40" : ""
          }`}
        >
          {PALETTE_GROUPS.map((group) => (
            <FamilyPreview key={group.family} group={group} palette={palette} />
          ))}
        </div>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center rounded-md bg-background/60">
            <div className="flex items-center gap-2 rounded-md border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("colorEditor.generatingPalette")}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
